import { Link } from 'react-router-dom';
import { Badge, Card } from '@safetyhub/ui';
import { CheckIcon } from '../components/icons';

// 요금제 데이터
const b2cPlans = [
  {
    name: '무료',
    price: 0,
    photoCount: 50,
    inspectionCount: '2~3',
    activeUsers: 1,
    target: '서비스 체험',
    featured: false,
  },
  {
    name: '개인',
    price: 30000,
    photoCount: 600,
    inspectionCount: '30',
    activeUsers: 1,
    target: '프리랜서',
    featured: true,
  },
  {
    name: '프로',
    price: 80000,
    photoCount: 1800,
    inspectionCount: '90',
    activeUsers: 3,
    target: '소규모 팀',
    featured: false,
  },
];

const b2bPlans = [
  {
    name: 'Starter',
    price: 100000,
    photoCount: 1200,
    inspectionCount: '60',
    activeUsers: 2,
    target: '소규모 사업장',
    featured: false,
  },
  {
    name: 'Basic',
    price: 250000,
    photoCount: 3000,
    inspectionCount: '150',
    activeUsers: 5,
    target: '중소기업',
    featured: true,
  },
  {
    name: 'Standard',
    price: 600000,
    photoCount: 9000,
    inspectionCount: '450',
    activeUsers: 15,
    target: '중견기업',
    featured: false,
  },
  {
    name: 'Enterprise',
    price: 1500000,
    photoCount: 24000,
    inspectionCount: '1,200',
    activeUsers: 40,
    target: '대기업',
    isEnterprise: true,
  },
];

// 주요 기능 데이터
const features = [
  {
    icon: CameraIcon,
    title: '사진 기반 위험요인 분석',
    description: '현장 사진을 촬영하면 위험 요소를 자동으로 분석하고 개선 사항을 제안합니다.',
  },
  {
    icon: DocumentIcon,
    title: '자동 보고서 생성',
    description: '분석 결과를 바탕으로 전문적인 안전점검 보고서가 자동으로 생성됩니다.',
  },
  {
    icon: ClipboardIcon,
    title: 'TBM 자동 생성',
    description: '점검 결과를 기반으로 작업 전 안전교육(TBM) 자료가 자동으로 만들어집니다.',
  },
  {
    icon: ChartIcon,
    title: '결재 워크플로우',
    description: '보고서 검토 및 결재 프로세스를 디지털화하여 업무 효율성을 높입니다.',
  },
];

// FAQ 데이터
const faqs = [
  {
    question: '무료 체험 후 자동으로 결제되나요?',
    answer: '아니요. 무료 요금제는 카드 등록 없이 이용 가능합니다. 사용량 소진 후 원하실 때 업그레이드하시면 됩니다.',
  },
  {
    question: '자동 분석 정확도는 어느 정도인가요?',
    answer: '자동 분석 결과는 참고용으로 제공되며, 최종 판단은 안전관리자가 검토 후 결정합니다. 지속적인 학습을 통해 정확도를 높여가고 있습니다.',
  },
  {
    question: '기존 데이터를 가져올 수 있나요?',
    answer: '기존 위험성평가 문서(Excel)를 업로드하면 자동으로 파싱하여 시스템에 반영합니다.',
  },
  {
    question: '여러 현장을 관리할 수 있나요?',
    answer: '네, 하나의 조직에서 여러 현장을 등록하고 관리할 수 있습니다. 현장별 담당자 지정도 가능합니다.',
  },
];

export default function Home() {
  const formatPrice = (price: number) => {
    if (price === 0) return '무료';
    return `₩${price.toLocaleString('ko-KR', {})}`;
  };

  return (
    <div className="min-h-screen bg-white">
      {/* 네비게이션 헤더 */}
      <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-b z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* 로고 */}
            <Link to="/" className="flex items-center">
              <span className="text-xl font-bold text-brand-600">SafetyHub</span>
            </Link>

            {/* 네비게이션 메뉴 */}
            <nav className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-ink-600 hover:text-ink-900 text-sm font-medium">
                주요기능
              </a>
              <a href="#pricing" className="text-ink-600 hover:text-ink-900 text-sm font-medium">
                Pricing
              </a>
              <a href="#help" className="text-ink-600 hover:text-ink-900 text-sm font-medium">
                Help
              </a>
            </nav>

            {/* 액션 버튼 */}
            <div className="flex items-center space-x-4">
              <Link
                to="/login"
                className="text-ink-600 hover:text-ink-900 text-sm font-medium"
              >
                로그인
              </Link>
              <Link
                to="/signup"
                className="bg-brand-600 text-white px-4 py-2 rounded-control text-sm font-medium hover:bg-brand-700 transition-colors"
              >
                무료로 시작
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* 히어로 섹션 */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-ink-900 leading-tight">
            스마트
            <br />
            <span className="text-brand-600">산업안전관리</span> 플랫폼
          </h1>
          <p className="mt-6 text-xl text-ink-600 max-w-2xl mx-auto">
            현장 사진만 촬영하면 위험 요소를 자동으로 분석하고
            <br className="hidden sm:block" />
            보고서를 자동으로 생성합니다.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/signup"
              className="w-full sm:w-auto bg-brand-600 text-white px-8 py-3 rounded-control text-lg font-medium hover:bg-brand-700 transition-colors"
            >
              무료로 시작하기
            </Link>
            <p className="text-sm text-ink-500">매월 2~3회 점검 무료 제공</p>
          </div>
        </div>
      </section>

      {/* 주요 기능 섹션 */}
      <section id="features" className="py-20 bg-ink-50 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-ink-900">주요 기능</h2>
            <p className="mt-4 text-lg text-ink-600">
              안전관리 업무 시간을 대폭 절감하세요
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-card shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 bg-brand-100 rounded-card flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-brand-600" />
                </div>
                <h3 className="text-lg font-semibold text-ink-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-ink-600 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 요금제 섹션 */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-ink-900">요금제</h2>
            <p className="mt-4 text-lg text-ink-600">
              필요에 맞는 요금제를 선택하세요 · 1회 점검 = 사진 20장
            </p>
          </div>

          {/* B2C 요금제 */}
          <div className="mb-16">
            <h3 className="text-lg font-semibold text-ink-700 mb-6 flex items-center">
              <Badge tone="brand" className="mr-3">
                개인용
              </Badge>
              프리랜서, 소규모 팀
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              {b2cPlans.map((plan) => (
                <div
                  key={plan.name}
                  className={`bg-white rounded-card p-6 ${
                    plan.featured
                      ? 'ring-2 ring-brand-600 shadow-lg'
                      : 'border border-ink-200'
                  }`}
                >
                  {plan.featured && (
                    <span className="inline-block bg-brand-600 text-white text-xs px-2 py-1 rounded-full mb-4">
                      인기
                    </span>
                  )}
                  <h4 className="text-xl font-bold text-ink-900">{plan.name}</h4>
                  <div className="mt-4">
                    <span className="text-3xl font-bold text-ink-900">
                      {formatPrice(plan.price)}
                    </span>
                    {plan.price > 0 && (
                      <span className="text-ink-500">/월</span>
                    )}
                  </div>
                  <ul className="mt-6 space-y-3">
                    <li className="flex items-center text-sm text-ink-600">
                      <CheckIcon className="w-5 h-5 text-green-500 mr-2" />월{' '}
                      {plan.inspectionCount}회 점검
                    </li>
                    <li className="flex items-center text-sm text-ink-600">
                      <CheckIcon className="w-5 h-5 text-green-500 mr-2" />
                      사진 {plan.photoCount.toLocaleString('ko-KR', {})}장 AI 분석
                    </li>
                    <li className="flex items-center text-sm text-ink-600">
                      <CheckIcon className="w-5 h-5 text-green-500 mr-2" />
                      {plan.activeUsers}명 활성 사용자
                    </li>
                  </ul>
                  <Link
                    to="/signup"
                    className={`mt-6 block w-full text-center py-2 px-4 rounded-control font-medium transition-colors ${
                      plan.featured
                        ? 'bg-brand-600 text-white hover:bg-brand-700'
                        : 'bg-ink-100 text-ink-900 hover:bg-ink-200'
                    }`}
                  >
                    시작하기
                  </Link>
                  <p className="mt-3 text-xs text-ink-500 text-center">
                    {plan.target}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* B2B 요금제 */}
          <div>
            <h3 className="text-lg font-semibold text-ink-700 mb-6 flex items-center">
              <Badge tone="emerald" className="mr-3">
                기업용
              </Badge>
              중소기업, 중견기업, 대기업
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {b2bPlans.map((plan) => (
                <div
                  key={plan.name}
                  className={`bg-white rounded-card p-6 ${
                    plan.featured
                      ? 'ring-2 ring-green-600 shadow-lg'
                      : 'border border-ink-200'
                  }`}
                >
                  {plan.featured && (
                    <span className="inline-block bg-green-600 text-white text-xs px-2 py-1 rounded-full mb-4">
                      추천
                    </span>
                  )}
                  <h4 className="text-xl font-bold text-ink-900">{plan.name}</h4>
                  <div className="mt-4">
                    <span className="text-3xl font-bold text-ink-900">
                      {formatPrice(plan.price)}
                    </span>
                    <span className="text-ink-500">/월</span>
                  </div>
                  <ul className="mt-6 space-y-3">
                    <li className="flex items-center text-sm text-ink-600">
                      <CheckIcon className="w-5 h-5 text-green-500 mr-2" />월{' '}
                      {plan.inspectionCount}회 점검
                    </li>
                    <li className="flex items-center text-sm text-ink-600">
                      <CheckIcon className="w-5 h-5 text-green-500 mr-2" />
                      사진 {plan.photoCount.toLocaleString('ko-KR', {})}장 AI 분석
                    </li>
                    <li className="flex items-center text-sm text-ink-600">
                      <CheckIcon className="w-5 h-5 text-green-500 mr-2" />
                      {plan.activeUsers}명 활성 사용자
                    </li>
                  </ul>
                  {plan.isEnterprise ? (
                    <a
                      href="mailto:sales@safetyhub.kr"
                      className="mt-6 block w-full text-center py-2 px-4 rounded-control font-medium bg-ink-900 text-white hover:bg-ink-800 transition-colors"
                    >
                      문의하기
                    </a>
                  ) : (
                    <Link
                      to="/signup"
                      className={`mt-6 block w-full text-center py-2 px-4 rounded-control font-medium transition-colors ${
                        plan.featured
                          ? 'bg-green-600 text-white hover:bg-green-700'
                          : 'bg-ink-100 text-ink-900 hover:bg-ink-200'
                      }`}
                    >
                      시작하기
                    </Link>
                  )}
                  <p className="mt-3 text-xs text-ink-500 text-center">
                    {plan.target}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Help/FAQ 섹션 */}
      <section id="help" className="py-20 bg-ink-50 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-ink-900">자주 묻는 질문</h2>
            <p className="mt-4 text-lg text-ink-600">
              궁금한 점이 있으신가요?
            </p>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <Card key={index} padding="xl">
                <h4 className="text-lg font-semibold text-ink-900">
                  {faq.question}
                </h4>
                <p className="mt-2 text-ink-600">{faq.answer}</p>
              </Card>
            ))}
          </div>
          <div className="mt-12 text-center">
            <p className="text-ink-600">
              더 궁금한 점이 있으신가요?{' '}
              <a
                href="mailto:support@safetyhub.kr"
                className="text-brand-600 hover:text-brand-700 font-medium"
              >
                support@safetyhub.kr
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* 푸터 */}
      <footer className="bg-ink-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-6 md:mb-0">
              <span className="text-xl font-bold">SafetyHub</span>
              <p className="mt-2 text-ink-400 text-sm">
                AI 기반 산업안전관리 플랫폼
              </p>
            </div>
            <div className="flex space-x-8 text-sm text-ink-400">
              <a href="#" className="hover:text-white">
                이용약관
              </a>
              <a href="#" className="hover:text-white">
                개인정보처리방침
              </a>
              <a href="mailto:support@safetyhub.kr" className="hover:text-white">
                고객지원
              </a>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-ink-800 text-center text-ink-400 text-sm">
            &copy; 2025 SafetyHub. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}

// 아이콘 컴포넌트들
function CameraIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  );
}

function DocumentIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  );
}

function ClipboardIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
      />
    </svg>
  );
}

function ChartIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
      />
    </svg>
  );
}
